import { Button, Space, Tag, Spin } from "antd";

import { ApolloCommandApi, CommandStatus } from "../../hooks/useApolloCommand";

/**
 * 경로 편집 패널(설계 §7, §11.2) — `<Canvas>` 밖 HTML.
 * 웨이포인트 목록 + 시작점 안내 + 편집 버튼 + 명령 상태 머신(ack 없는 프로토콜이라 상태를 그대로 노출).
 * 주행 개시/정지(ENTER_AUTO_MODE/DISENGAGE)는 Stage 3 에서 별도 패널로 추가한다(2단계 분리, §10.3).
 */

const STATUS_META: { [k in CommandStatus]: { label: string; color: string } } = {
    editing: { label: "편집 중", color: "default" },
    PENDING: { label: "차량 전달 대기", color: "processing" },
    SENT: { label: "차량 수신 — 결과 확인 중", color: "processing" },
    ACK: { label: "라우팅 성공", color: "success" },
    FAILED: { label: "실패", color: "error" },
    EXPIRED: { label: "만료(미전달)", color: "warning" },
};

export default function RouteEditPanel({
    api,
    carId,
}: {
    api: ApolloCommandApi;
    carId: number | null;
}) {
    const { waypoints, removeLast, clear, sendRoute, status, sending, error, commandId } = api;
    const n = waypoints.length;
    const meta = STATUS_META[status];
    const busy = status === "PENDING" || status === "SENT" || sending;
    const canSend = carId != null && n > 0 && !busy;
    const editDisabled = n === 0 || busy;
    // 어두운 패널용 버튼 스타일(비활성 시 검게 보이지 않도록 명시). antd 테마 disabled 색을 인라인으로 덮는다.
    const editBtnStyle = (disabled: boolean) => ({
        background: disabled ? "transparent" : "#1f2836",
        borderColor: disabled ? "#2a3446" : "#3a4a63",
        color: disabled ? "#5a6b83" : "#dbe4f2",
    });

    return (
        <div
            style={{
                position: "absolute",
                top: 12,
                right: 12,
                zIndex: 12,
                width: 268,
                background: "rgba(10,16,24,0.92)",
                border: "1px solid #2a3446",
                borderRadius: 8,
                padding: 12,
                color: "#dbe4f2",
            }}
        >
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>경로 편집</div>

            <div style={{ fontSize: 12, color: "#8a99b0", marginBottom: 8 }}>
                시작점: <span style={{ color: "#dbe4f2" }}>현재 차량 위치(자동)</span>
            </div>

            {/* 웨이포인트 목록 */}
            <div style={{ maxHeight: 180, overflowY: "auto", marginBottom: 8 }}>
                {n === 0 && (
                    <div style={{ fontSize: 12.5, color: "#6d7c93", padding: "10px 0", textAlign: "center" }}>
                        지면을 클릭해 경유지·목적지를 지정하세요.
                    </div>
                )}
                {waypoints.map((w, i) => {
                    const isDest = i === n - 1;
                    return (
                        <div
                            key={i}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                fontSize: 12.5,
                                padding: "3px 0",
                                borderBottom: "1px solid #1c2532",
                            }}
                        >
                            <span
                                style={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: 4,
                                    flexShrink: 0,
                                    background: isDest ? "#ff4d4f" : "#39d0ff",
                                    color: "#0a0f18",
                                    fontSize: 11,
                                    fontWeight: 700,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                {isDest ? "★" : i + 1}
                            </span>
                            <span style={{ color: isDest ? "#ffd0d0" : "#cfe6ff" }}>
                                {isDest ? "목적지" : `경유 ${i + 1}`}
                            </span>
                            <span style={{ marginLeft: "auto", color: "#7f8da3", fontVariantNumeric: "tabular-nums" }}>
                                ({w[0].toFixed(1)}, {w[1].toFixed(1)})
                            </span>
                        </div>
                    );
                })}
            </div>

            <Space size={6} style={{ marginBottom: 10 }}>
                <Button size="small" onClick={removeLast} disabled={editDisabled} style={editBtnStyle(editDisabled)}>마지막 취소</Button>
                <Button size="small" onClick={clear} disabled={editDisabled} style={editBtnStyle(editDisabled)}>전체 지우기</Button>
            </Space>

            {/* 전송 + 상태 */}
            <Button
                type="primary"
                block
                disabled={!canSend}
                onClick={() => carId != null && sendRoute(carId)}
                style={!canSend ? { background: "#1a2230", borderColor: "#2a3446", color: "#5a6b83" } : undefined}
            >
                {busy ? <Spin size="small" /> : "경로 전송"}
            </Button>

            {/* 상태는 전송 이후에만 노출(§7 상태머신) — 편집 중엔 노이즈라 숨김 */}
            {status !== "editing" && (
                <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                    <span style={{ color: "#8a99b0" }}>상태</span>
                    <Tag color={meta.color}>{meta.label}</Tag>
                </div>
            )}
            {commandId && (
                <div style={{ marginTop: 4, fontSize: 11, color: "#6d7c93", wordBreak: "break-all" }}>
                    명령 {commandId.slice(0, 8)}
                </div>
            )}
            {error && <div style={{ marginTop: 6, fontSize: 12, color: "#ff7875" }}>{error}</div>}

            <div style={{ marginTop: 10, fontSize: 11, color: "#6d7c93", lineHeight: 1.5 }}>
                ⚠ 경로 전송은 목표 경로만 차량에 보냅니다. 주행 개시(자율주행 시작)는 별도 단계입니다.
            </div>
        </div>
    );
}
